// The saved configuration, read by section.
//
// The source of truth has always been what is saved, not the form: /settings writes it and every
// other page reads it. But the reading went through legacy/config.js, which is a form adapter, so
// anything that wanted to know the engine depth had to go through a module full of DOM elements
// that only exist on one page. The tools coming next are not that page.
//
// So: this holds the defaults and the reads, and knows nothing about forms. legacy/config.js is
// what writes it from /settings, and what applies a configuration to those controls.

import { STORAGE_KEYS, DEFAULTS } from "../state/index.ts";

export type Side = "white" | "black";

export interface SparringConfig {
    since: string;
    until: string;
    speeds: string[];
    ratings: string[];
    sparringSkill: string;
}

export interface EngineConfig {
    engineEnabled: boolean;
    engineSource: string;
    engineUrl: string;
    engineDepth: number;
    engineMultiPV: number;
}

export interface BoardConfig {
    autoPlay: boolean;
    boardSide: Side;
}

export interface PlayerConfig {
    lichessUsername: string;
}

/** Everything as it is stored on this device: one flat object, as it has always been. */
export type StoredConfig = Record<string, unknown>;

export function stored(): StoredConfig {
    try {
        const raw = localStorage.getItem(STORAGE_KEYS.config);
        return raw ? (JSON.parse(raw) as StoredConfig) ?? {} : {};
    } catch (err) {
        return {};
    }
}

function write(config: StoredConfig): void {
    try {
        localStorage.setItem(STORAGE_KEYS.config, JSON.stringify(config));
    } catch (err) {
        // A browser that will not remember it still works for this visit
    }
}

/** Merges into what is stored. The only way anything changes the configuration of this device. */
export function patch(changes: StoredConfig): StoredConfig {
    const next = { ...stored(), ...changes };
    write(next);
    return next;
}

const number = (value: unknown, fallback: number): number => {
    const parsed = parseInt(String(value ?? ""), 10);
    return Number.isFinite(parsed) ? parsed : fallback;
};

const list = (value: unknown, fallback: string[]): string[] =>
    Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : fallback;

// ---------------------------------------------------------------------------------------------
// By section
// ---------------------------------------------------------------------------------------------

/** Who the sparring partner plays like on the user's own lines. */
export function sparring(config: StoredConfig = stored()): SparringConfig {
    return {
        since: typeof config.since === "string" ? config.since : "",
        until: typeof config.until === "string" ? config.until : "",
        speeds: list(config.speeds, [...DEFAULTS.speeds]),
        ratings: list(config.ratings, [...DEFAULTS.ratings]),
        sparringSkill: typeof config.sparringSkill === "string" ? config.sparringSkill : "amateur",
    };
}

export function engine(config: StoredConfig = stored()): EngineConfig {
    return {
        engineEnabled: typeof config.engineEnabled === "boolean" ? config.engineEnabled : DEFAULTS.engineEnabled,
        engineSource: typeof config.engineSource === "string" ? config.engineSource : DEFAULTS.engineSource,
        engineUrl: typeof config.engineUrl === "string" ? config.engineUrl : "",
        // Both are typed as strings in the form and have always been stored that way
        engineDepth: number(config.engineDepth, number(DEFAULTS.engineDepth, 20)),
        engineMultiPV: number(config.engineMultiPV, number(DEFAULTS.engineMultiPV, 1)),
    };
}

export function board(config: StoredConfig = stored()): BoardConfig {
    const side = config.boardSide === "black" ? "black" : "white";
    return {
        autoPlay: typeof config.autoPlay === "boolean" ? config.autoPlay : DEFAULTS.boardAutoplay,
        boardSide: side,
    };
}

export function player(config: StoredConfig = stored()): PlayerConfig {
    return { lichessUsername: typeof config.player === "string" ? config.player : "" };
}

/**
 * What one tool keeps for itself: the difficulty of a memory game, the strength of a sparring
 * partner, how much help the blindfold gives. Stored under its own key so two tools never argue
 * over the same name.
 */
export function tool<T extends Record<string, unknown>>(slug: string, defaults: T): T {
    const tools = stored().tools;
    const own = tools && typeof tools === "object" ? (tools as Record<string, unknown>)[slug] : null;
    return { ...defaults, ...(own && typeof own === "object" ? own as T : {}) };
}

export function saveTool(slug: string, changes: Record<string, unknown>): void {
    const config = stored();
    const tools = (config.tools && typeof config.tools === "object" ? config.tools : {}) as Record<string, unknown>;
    const own = (tools[slug] && typeof tools[slug] === "object" ? tools[slug] : {}) as Record<string, unknown>;
    patch({ tools: { ...tools, [slug]: { ...own, ...changes } } });
}
